import React, { useState, useEffect } from 'react'
import {
  Btn,
  CustomDropdown,
  CustomModal,
  InputNumeroInteiro,
  CustomInputTextArea,
} from '../../../../../components'
import { getEmissoresByTps } from '../../../../../services/api/https/settings/emissor-cte.http'
import { postEmitCTeGlobalizado } from '../../../../../services/api/https/deliveries/cte.http'
import { getCfops } from '../../../../../services/api/https/settings/cfop.http'
import ValoresFiscaisCte from '../ValoresFiscaisCte/ValoresFiscaisCte'
import {
  ERROS_VALORES_FISCAIS,
  VALORES_FISCAIS_INICIAIS,
  erroValoresFiscais,
  montarComponentesValor,
  novoComponente,
} from '../ValoresFiscaisCte/valoresFiscaisCte.util'

const inicialState = {
  emissor_id: null,
  cfop_id: null,
  numero_ordem_coleta: null,
  ...VALORES_FISCAIS_INICIAIS,
  observacoes: null,
}

const errors = {
  emissor_id: 'O campo Emissor é obrigatório',
  cfop_id: 'O campo CFOP é obrigatório',
  numero_ordem_coleta: 'O campo Nº Ordem Coleta é obrigatório',
  ...ERROS_VALORES_FISCAIS,
}

export default function ModalEmitirCteGlobalizado({
  selectedTp,
  visible,
  valorFrete,
  // Componentes do valor consolidados por regra entre todas as notas selecionadas
  componentes: componentesIniciais = null,
  deliveriesIds,
  onModalClose,
  onModalSave,
  onError,
}) {
  // Hooks
  const [isLoading, setIsLoading] = useState(false)
  const [state, setState] = useState(inicialState)
  const [componentes, setComponentes] = useState([])
  const [emissores, setEmissores] = useState({ emissores: [], selected: null })
  const [cfops, setCfops] = useState({ cfops: [], selected: null })

  // Atualizar valores de frete quando o modal abrir
  useEffect(() => {
    if (visible && valorFrete !== null) {
      setComponentes((componentesIniciais || []).map(c => novoComponente(c.nome, c.valor)))
      setState(prevState => ({
        ...prevState,
        valor_total: valorFrete,
        valor_receber: valorFrete,
        icms_base_calculo: valorFrete,
        ibs_cbs_base_calculo: valorFrete,
        observacoes: '',
      }))
    }
  }, [visible, valorFrete, componentesIniciais])

  useEffect(() => {
    async function fetchData() {
      const [emissores, cfops] = await Promise.all([getEmissoresByTps(selectedTp), getCfops()])

      // Mapear CFOPs para adicionar campo concatenado para facilitar a busca
      const cfopsMapeados = cfops.map(cfop => ({
        ...cfop,
        codigoNatureza: `${cfop.codigo} - ${cfop.natureza}`
      }))

      // Encontrar as opções selecionadas anteriormente no state
      const emissorSelecionado = emissores.find(item => item.id === state.emissor_id)
      const cfopSelecionado = cfopsMapeados.find(item => item.id === state.cfop_id)

      setEmissores(prevState => ({
        ...prevState,
        emissores,
        selected: emissorSelecionado || null,
      }))
      setCfops(prevState => ({
        ...prevState,
        cfops: cfopsMapeados,
        selected: cfopSelecionado || null,
      }))
    }
    fetchData()
  }, [selectedTp, state.emissor_id, state.cfop_id])

  // Events
  const onBtnSalvarClicked = async () => {
    try {
      Object.keys(state).forEach(key => {
        if (key !== 'observacoes' && state[key] === null) {
          throw new Error(errors[key])
        }
      })

      if (state.observacoes && state.observacoes.length > 2000) {
        throw new Error('O campo Observações deve ter no máximo 2000 caracteres')
      }

      const erroFiscal = erroValoresFiscais(state)
      if (erroFiscal) throw new Error(erroFiscal)

      if (!deliveriesIds || deliveriesIds.length < 5) {
        throw new Error('Um CTe Globalizado deve conter no mínimo 5 destinatários')
      }

      setIsLoading(true)

      const componentesValor = montarComponentesValor(componentes)

      await postEmitCTeGlobalizado({
        ...state,
        deliveries_ids: deliveriesIds,
        ...(componentesValor.length ? { componentes_valor: componentesValor } : {}),
      })

      onModalSave()
    } catch (error) {
      onError(error)
    } finally {
      setIsLoading(false)
    }
  }

  // Render
  return (
    <CustomModal
      isVisible={visible}
      header={'Emitir CTe Globalizado'}
      width={800}
      onClose={onModalClose}
      contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
    >
      <form className="p-grid" onSubmit={e => e.preventDefault()}>
        <div className="p-col-12">
          <div
            style={{
              backgroundColor: '#e3f2fd',
              padding: '10px 15px',
              borderRadius: '5px',
              marginBottom: '10px',
            }}
          >
            <strong>Entregas selecionadas:</strong> {deliveriesIds.length || 0}
          </div>
        </div>

        <div className="p-md-6 p-col-12">
          <CustomDropdown
            optionLabel="razao_social"
            options={emissores.emissores}
            value={emissores.selected}
            label="Emissor:*"
            filter={true}
            onChange={value => {
              setState(prevState => {
                prevState.emissor_id = value.id
                return { ...prevState }
              })
              setEmissores(prevState => ({ ...prevState, selected: value }))
            }}
          />
        </div>
        <div className="p-md-6 p-col-12">
          <CustomDropdown
            optionLabel="codigoNatureza"
            options={cfops.cfops}
            value={cfops.selected}
            label="CFOP:*"
            filter={true}
            onChange={value => {
              setState(prevState => {
                prevState.cfop_id = value.id
                return { ...prevState }
              })
              setCfops(prevState => ({ ...prevState, selected: value }))
            }}
            tooltip={'Código Fiscal de Operações e Prestações'}
          />
        </div>
        <div className="p-md-6 p-col-12">
          <InputNumeroInteiro
            label="Nº Ordem Coleta:*"
            marginTop={10}
            value={state.numero_ordem_coleta}
            onChange={value => setState({ ...state, numero_ordem_coleta: value })}
          />
          <span style={{ color: 'gray', fontSize: 12 }}>
            Número gerado pela transportadora associado a ordem de coleta
          </span>
        </div>

        <ValoresFiscaisCte
          key={visible ? 'aberto' : 'fechado'}
          state={state}
          onStateChange={setState}
          componentes={componentes}
          onComponentesChange={setComponentes}
          onError={onError}
          componentesHint="Nome do componente: máximo de 15 caracteres (limite da SEFAZ). Valores consolidados por regra entre todas as notas selecionadas."
        />

        <div className="p-col-12">
          <CustomInputTextArea
            label="Observações"
            marginTop={10}
            rows={4}
            value={state.observacoes || ''}
            onChange={value => setState({ ...state, observacoes: value })}
          />
        </div>

        <div className="p-col-12" style={{ display: 'flex', justifyContent: 'end' }}>
          <Btn onClick={onBtnSalvarClicked} isLoading={isLoading}>
            Salvar
          </Btn>
        </div>
      </form>
    </CustomModal>
  )
}
